import { Head, Link, router } from "@inertiajs/react"
import { Contact, Edit, Plus, Trash2, Users } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { agentPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property, Suite } from "@/types"

interface Props {
  property: Property
  suites: Suite[]
}

const statusVariant: Record<Suite["status"], "default" | "secondary" | "destructive"> = {
  available: "default",
  under_negotiation: "secondary",
  leased: "destructive",
}

const statusLabel: Record<Suite["status"], string> = {
  available: "Available",
  under_negotiation: "Under Negotiation",
  leased: "Leased",
}

export default function Show({ property, suites }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "My Properties", href: agentPropertiesPath() },
    { title: property.name, href: `/agent/properties/${property.id}` },
  ]

  const handleDeleteSuite = (suiteId: number) => {
    if (confirm("Remove this suite?")) {
      router.delete(`/agent/properties/${property.id}/suites/${suiteId}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={property.name} />

      <div className="space-y-6 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{property.name}</h1>
            <p className="text-muted-foreground text-sm">
              {property.address}, {property.city}, {property.state} {property.zip_code}
            </p>
            {property.description && (
              <p className="mt-2 max-w-2xl text-sm">{property.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" render={<Link href={`/agent/properties/${property.id}/leasing_contacts`} />}>
              <Contact className="mr-2 h-4 w-4" /> Leasing Contacts
            </Button>
            <Button variant="outline" render={<Link href={`/agent/properties/${property.id}/tenants`} />}>
              <Users className="mr-2 h-4 w-4" /> Tenants
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle>Suites</CardTitle>
            <Button size="sm" render={<Link href={`/agent/properties/${property.id}/suites/new`} />}>
              <Plus className="mr-2 h-4 w-4" /> Add Suite
            </Button>
          </CardHeader>
          <CardContent>
            {suites.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Plus />
                  </EmptyMedia>
                  <EmptyTitle>No suites yet</EmptyTitle>
                  <EmptyDescription>Add the first suite to this property.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Suite</TableHead>
                    <TableHead>Floor</TableHead>
                    <TableHead>Sq Ft</TableHead>
                    <TableHead>Monthly Rent</TableHead>
                    <TableHead>Available On</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suites.map((suite) => (
                    <TableRow key={suite.id}>
                      <TableCell className="font-medium">{suite.suite_number}</TableCell>
                      <TableCell>{suite.floor ?? "—"}</TableCell>
                      <TableCell>{suite.sqft ? Number(suite.sqft).toLocaleString() : "—"}</TableCell>
                      <TableCell>
                        {suite.monthly_rent
                          ? `$${Number(suite.monthly_rent).toLocaleString()}`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {suite.available_on
                          ? new Date(suite.available_on).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant[suite.status]}>
                          {statusLabel[suite.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            render={<Link href={`/agent/properties/${property.id}/suites/${suite.id}/edit`} />}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteSuite(suite.id)}
                          >
                            <Trash2 className="text-destructive h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
